import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader } from 'lucide-react';
import { db } from '../firebase';
import { collection, writeBatch, query, where, getDocs, doc } from 'firebase/firestore';

// This is the new "top-tier" modal that uses the "batch write" method.
const RecurringBookingModal = ({ stadium, onClose, onSave }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(''); // Loading status text

  // Form State
  const [label, setLabel] = useState('');
  const [selectedSportId, setSelectedSportId] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const [daysOfWeek, setDaysOfWeek] = useState([]); // Sun=0, Mon=1, ...
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const allDays = t('recurringmodal.days', { returnObjects: true }); // ["Sun", "Mon", ...]

  // --- Helper function to generate slots ---
  const generateSlotsForSport = (sport) => {
    const slots = [];
    if (!sport || !sport.openTime || !sport.closeTime || !sport.duration) return slots;
    const durationMin = parseInt(sport.duration);
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    const minutesToTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
      const minutes = (totalMinutes % 60).toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    const openMin = timeToMinutes(sport.openTime);
    const closeMin = timeToMinutes(sport.closeTime);
    let currentMin = openMin;
    let slotIndex = 0;
    while (currentMin + durationMin <= closeMin) {
      const startTime = minutesToTime(currentMin);
      const endTime = minutesToTime(currentMin + durationMin);
      const slotId = `${sport.id}-slot-${slotIndex}`;
      slots.push({ id: slotId, startTime, endTime, price: sport.pricePerSlot });
      currentMin += durationMin;
      slotIndex++;
    }
    return slots;
  };

  // --- This effect runs when the owner selects a sport ---
  useEffect(() => {
    if (selectedSportId) {
      const sport = stadium.sports.find(s => s.id === selectedSportId);
      const slots = generateSlotsForSport(sport);
      setAvailableSlots(slots);
      setSelectedSlotIds([]); // Reset selection
    } else {
      setAvailableSlots([]);
      setSelectedSlotIds([]);
    }
  }, [selectedSportId, stadium.sports]);

  const handleDayToggle = (dayIndex) => {
    setDaysOfWeek(prev => {
      const newDays = [...prev];
      if (newDays.includes(dayIndex)) {
        return newDays.filter(d => d !== dayIndex);
      } else {
        return [...newDays, dayIndex];
      }
    });
  };

  const handleSlotToggle = (slotId) => {
    setSelectedSlotIds(prev => {
      const newSlots = [...prev];
      if (newSlots.includes(slotId)) {
        return newSlots.filter(id => id !== slotId);
      } else {
        return [...newSlots, slotId];
      }
    });
  };

  const handleSubmit = async () => {
    setError('');
    setStatus('');
    
    // 1. Standard Validation
    if (!label || !selectedSportId || selectedSlotIds.length === 0 || daysOfWeek.length === 0 || !startDate || !endDate) {
      setError(t('recurringmodal.errorFields'));
      return;
    }
    if (endDate <= startDate) {
      setError(t('recurringmodal.errorDates'));
      return;
    }
    setLoading(true);

    try {
      const sport = stadium.sports.find(s => s.id === selectedSportId);
      const allSlots = generateSlotsForSport(sport);
      const selectedSlots = allSlots.filter(s => selectedSlotIds.includes(s.id));

      // --- 2. Generate all dates for the rule ---
      setStatus(t('recurringmodal.findingDates'));
      const datesToBook = [];
      let currentDate = new Date(startDate); // Use local date only
      const lastDate = new Date(endDate); // Use local date only
      while (currentDate <= lastDate) {
        const dayOfWeek = currentDate.getDay(); // Sun=0, Mon=1...
        if (daysOfWeek.includes(dayOfWeek)) {
          // Format as YYYY-MM-DD (local date)
          const yyyy = currentDate.getFullYear();
          const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
          const dd = String(currentDate.getDate()).padStart(2, '0');
          datesToBook.push(`${yyyy}-${mm}-${dd}`);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (datesToBook.length === 0) {
        setError(t('recurringmodal.errorNoDates'));
        setLoading(false);
        return;
      }
      
      // --- 3. Check for Conflicts ---
      // We must check in batches of 30 (Firestore 'in' query limit)
      setStatus(t('recurringmodal.checkingConflicts', { count: datesToBook.length }));
      const dateBatches = [];
      for (let i = 0; i < datesToBook.length; i += 30) {
        dateBatches.push(datesToBook.slice(i, i + 30));
      }

      const bookedSlotsOnDate = {}; // { "2025-12-01": Set("slot-1", "slot-2") }
      
      for (const batchOfDates of dateBatches) {
        const q = query(
          collection(db, 'bookings'),
          where('stadiumId', '==', stadium.id),
          where('date', 'in', batchOfDates),
          where('sportId', '==', sport.id)
        );
        const existingBookingsSnapshot = await getDocs(q);
        
        existingBookingsSnapshot.forEach(doc => {
          const data = doc.data();
          if (!bookedSlotsOnDate[data.date]) {
            bookedSlotsOnDate[data.date] = new Set();
          }
          // Check for both customer and recurring booking conflicts
          if (data.type === 'customer' || data.type === 'recurring') {
             bookedSlotsOnDate[data.date].add(data.slotId);
          }
        });
      }

      // Now, check for the first conflict
      for (const date of datesToBook) {
        if (bookedSlotsOnDate[date]) {
          for (const slotId of selectedSlotIds) {
            if (bookedSlotsOnDate[date].has(slotId)) {
              setError(t('recurringmodal.conflictError', { date }));
              setLoading(false);
              return;
            }
          }
        }
      }

      // --- 4. No Conflicts. Create Batch Write ---
      setStatus(t('recurringmodal.savingBookings', { count: selectedSlotIds.length * datesToBook.length }));
      const batch = writeBatch(db);

      // Generate a unique ID for this recurring booking group
      const recurringGroupId = `recurring_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      for (const date of datesToBook) {
        for (const slot of selectedSlots) {
          const newBookingDocRef = doc(collection(db, 'bookings'));
          
          batch.set(newBookingDocRef, {
            stadiumId: stadium.id,
            stadiumName: stadium.name,
            sportId: sport.id,
            sportName: sport.name,
            
            label: label, // This is the "Academy" label
            type: 'recurring', // This is the new key
            recurringGroupId: recurringGroupId, // Link all instances together
            
            date: date,
            slotId: slot.id,
            startTime: slot.startTime,
            endTime: slot.endTime,
            
            price: 0, 
            customerName: label,
            customerPhone: stadium.phone,
            numPlayers: sport.capacity,
            
            createdAt: new Date().toISOString(),
            status: 'confirmed',
            ownerEmail: stadium.ownerEmail,
          });
        }
      }

      // --- 5. Commit the Batch ---
      await batch.commit();

      onSave(); // This closes the modal and refreshes the dashboard
    
    } catch (err) {
      if (err.code === 'invalid-argument') {
        setError(t('recurringmodal.errorRange'));
      } else {
        console.error("Error creating recurring bookings:", err);
        setError(t('recurringmodal.errorSubmit'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-[60] animate-fade-in overflow-y-auto">
      <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-neutral-700/50 max-w-2xl w-full p-8 animate-slide-up relative my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
        <button 
          onClick={onClose}
          className={`absolute top-4 ${i18n.language === 'ar' ? 'left-4' : 'right-4'} p-2 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition`}
        >
          <X size={24} />
        </button>

        <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-6">{t('recurringmodal.modalTitle')}</h2>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4 text-center">
            {error}
          </div>
        )}

        <div className="space-y-5">
          {/* Label */}
          <div>
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.label')}</label>
            <input
              type="text"
              name="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
              placeholder={t('recurringmodal.labelPlaceholder')}
            />
          </div>

          {/* Sport Select */}
          <div>
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.sport')}</label>
            <select
              name="sportId"
              value={selectedSportId}
              onChange={(e) => setSelectedSportId(e.target.value)}
              className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
            >
              <option value="">{t('recurringmodal.sportPlaceholder')}</option>
              {stadium.sports.map(sport => (
                <option key={sport.id} value={sport.id}>
                  {sport.name} ({sport.duration} {t('detailpage.minutes', { count: '' })})
                </option>
              ))}
            </select>
          </div>

          {/* Time Slot Multi-Select */}
          {availableSlots.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.modalSlotLabel')}</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
                {availableSlots.map(slot => {
                  const isSelected = selectedSlotIds.includes(slot.id);
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => handleSlotToggle(slot.id)}
                      className={`p-2 rounded-lg text-sm font-semibold transition ${
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                      }`}
                    >
                      {slot.startTime} - {slot.endTime}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Days of Week */}
          <div>
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.daysOfWeek')}</label>
            <div className="flex flex-wrap gap-2">
              {allDays.map((dayName, index) => {
                const dayValue = index; // Sun=0, Mon=1...
                const isSelected = daysOfWeek.includes(dayValue);
                return (
                  <button
                    key={dayValue}
                    type="button"
                    onClick={() => handleDayToggle(dayValue)}
                    className={`px-4 py-2 rounded-full font-semibold transition ${
                      isSelected
                        ? 'bg-primary text-white'
                        : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                    }`}
                  >
                    {dayName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date Pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.startDate')}</label>
              <input
                type="date"
                name="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('recurringmodal.endDate')}</label>
              <input
                type="date"
                name="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-4 border-2 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition"
            >
              {t('recurringmodal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>{status || t('recurringmodal.savingButton')}</span>
                </>
              ) : (
                t('recurringmodal.saveButton')
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RecurringBookingModal;